/**
 * Live-state target registry - Milestone 1 of the Rig State primitive
 * (2026-08-15, PLAN.md QUEUED "Rig States" > "Resequenced 2026-08-15").
 *
 * A small, deliberately NOT-generic registry of the targets that already
 * have a reactive live-value source elsewhere in the app (transpose/session.ts,
 * autotune/session.ts - the same signals Display.tsx already reads for its
 * own live readout). Backs the Timeline editor's live-state drawer: each
 * entry's `read()` is what the drawer shows, and what a drag-capture reads
 * at drag-start time to build a `TargetValueStep`.
 *
 * Deliberately scoped to these 3 targets, not a general MIDI-CC/DMX/OSC
 * send-cache (that's Milestone 2's job, when the full Rig State bank layer
 * needs arbitrary targets, not just these three named live-value sources).
 * Adding a 4th target later is a new array entry, not a redesign.
 */

import { dropTuning, capoForTranspose } from '../transpose/session.ts';
import { noteName, type AutotuneScale } from '../autotune/session.ts';
import { autotuneKeyShown, autotuneScaleShown } from '../autotune/readback.ts';
import { setlistState, observedTranspose } from '../setlist/state.ts';

/** "+7" / "0" / "-5" - same explicit-sign convention as Display.tsx's local
 *  `signed()` / Transpose.tsx's `semitoneLabel` (each keeps its own copy;
 *  the project's own convention for this one-liner is local, not shared). */
function signed(n: number): string {
  return n > 0 ? `+${n}` : String(n);
}

const SCALE_LABEL: Record<AutotuneScale, string> = {
  chrom: 'CHROM', maj: 'MAJ', min: 'NAT MIN', minHarm: 'HARM MIN',
  dorian: 'DORIAN',
};

/**
 * What the live-state drawer shows for one target, or `null` when there is
 * no value.
 *
 * ⚠️ THE COMMENT HERE USED TO SAY UNKNOWN "DOESN'T APPLY," and that stopped
 * being true on 2026-09-21. It read: *"every one of these targets is a local
 * reactive signal, never an unreadable open-loop external value."* That was
 * accurate when written and was precisely the defect - the targets WERE all
 * local latches, so a drag-capture recorded what this device last sent rather
 * than what the rig actually was, and an OPSetlist song change could not move
 * the drawer. Three of the four now read authorities, so `null` genuinely
 * means "not reported yet" for those.
 *
 * Consumers already handled `null` (the autotune entries always could return
 * it), which is the only reason this repoint was small: `RigStateManager`
 * filters `read() !== null` out of the capture list, and the Timeline drawer
 * sets `draggable={reading() !== null}` with an early return in its
 * drag-start. What CHANGED is that null is now reachable for transpose and
 * capo too, and that it means something different per `source`.
 */
export interface LiveTargetReading {
  display: string;
  value: number | string;
}

/**
 * Where a target's value comes from, which is the distinction this whole
 * registry got wrong until 2026-09-21.
 *
 * - `readback` - an AUTHORITY told us. OPBridge reports what it actually did
 *   to Live, so this survives someone else moving the rig (an OPSetlist song
 *   change, a hand on a knob in Ableton). `null` means *"nobody has told us
 *   yet"*.
 * - `local` - a LATCH. A value this device remembers because WE set it, held
 *   in `localStorage`. Only a tap or a Rig State recall writes it, so it goes
 *   stale the instant anything else changes the rig, and it goes stale
 *   SILENTLY. `null` means *"not set on this device this session"*.
 *
 * Carried on the target rather than inferred, because the two produce
 * different honest wording for an absent value and the drawer has to say
 * which one it means. Conflating them is how a readout starts claiming the
 * rig is somewhere it is not.
 */
export type LiveTargetSource = 'readback' | 'local';

export interface LiveTarget {
  /** Stable id - becomes `TargetValueStep.targetId` on capture. */
  id: string;
  /** Drawer row label. */
  label: string;
  /** Authority-reported or device-local. See {@link LiveTargetSource}. */
  source: LiveTargetSource;
  read(): LiveTargetReading | null;
}

export const LIVE_TARGETS: LiveTarget[] = [
  {
    id: 'transpose',
    label: 'Transpose',
    source: 'readback',
    read: () => {
      const t = observedTranspose(setlistState());
      if (t === null) return null;
      return { display: `${signed(t)} · drop ${dropTuning()}`, value: t };
    },
  },
  {
    id: 'capo',
    label: 'Capo',
    source: 'readback',
    read: () => {
      const t = observedTranspose(setlistState());
      if (t === null) return null;
      const fret = capoForTranspose(t);
      return { display: `${signed(fret)} · drop ${dropTuning()}`, value: fret };
    },
  },
  {
    id: 'autotuneKey',
    label: 'Waves Key',
    source: 'readback',
    read: () => {
      // `autotune/readback.ts`, the SAME source as the Waves tile: the song's
      // key unless a tap is newer - what the plugin was last told, and so what
      // a capture must recall.
      const pc = autotuneKeyShown();
      return pc === null ? null : { display: noteName(pc), value: pc };
    },
  },
  {
    id: 'autotuneScale',
    label: 'Waves Scale',
    // From the song too: OPBridge sends the key's mode as the scale on every
    // song change that carries a key (see `autotune/readback.ts`). A tapped
    // HARM or DOR newer than the song is kept, so the three-valued mode never
    // overwrites a five-valued choice made after it.
    source: 'readback',
    read: () => {
      const scale = autotuneScaleShown();
      return scale === null ? null : { display: SCALE_LABEL[scale], value: scale };
    },
  },
];

export function getLiveTarget(id: string): LiveTarget | undefined {
  return LIVE_TARGETS.find((t) => t.id === id);
}
